import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { DatePeriod } from '../../types/DatePeriod';
import { DurationUnit } from '../../types/DurationUnit';
import { Input } from '../../types/Input';
import { Nullable } from '../../types/Nullable';
import { Option } from '../../types/Option';
import { Layout } from '../Layout/Layout';
import { NumberInput } from '../NumberInput/NumberInput';
import { Select } from '../Select/Select';

const defaultDatePeriod: DatePeriod = { value: null, unit: null };

type Props = Input<DatePeriod>;

export const DatePeriodInput = memo(
  ({ name, value: initialDatePeriod, onChange }: Props) => {
    const intl = useIntl();

    const options: Option<DurationUnit>[] = [
      {
        label: intl.formatMessage({
          id: 'DatePeriodInput.day',
          defaultMessage: 'Day',
          description: 'Label for the day unit in date period input',
        }),
        value: 'days',
      },
      {
        label: intl.formatMessage({
          id: 'DatePeriodInput.week',
          defaultMessage: 'Week',
          description: 'Label for the week unit in date period input',
        }),
        value: 'weeks',
      },
      {
        label: intl.formatMessage({
          id: 'DatePeriodInput.month',
          defaultMessage: 'Month',
          description: 'Label for the month unit in date period input',
        }),
        value: 'months',
      },
      {
        label: intl.formatMessage({
          id: 'DatePeriodInput.year',
          defaultMessage: 'Year',
          description: 'Label for the year unit in date period input',
        }),
        value: 'years',
      },
    ];

    const datePeriod = initialDatePeriod || defaultDatePeriod;
    const { value, unit } = datePeriod;

    const handleValueChange = (updatedValue: Nullable<number>) => {
      onChange({ value: updatedValue, unit });
    };

    const handleUnitChange = (updatedUnit: Nullable<DurationUnit>) => {
      onChange({ value, unit: updatedUnit });
    };

    return (
      <Layout align="center" minW="input.min-width" spacing={2}>
        <NumberInput
          name={`${name}-value`}
          min={0}
          placeholder={intl.formatMessage({
            id: 'DatePeriodInput.placeholder.value',
            defaultMessage: 'Value',
            description: 'Placeholder for value input in date period input',
          })}
          value={value}
          width="input.s.width"
          onChange={handleValueChange}
        />

        <Select
          enablePortal={true}
          isClearable={false}
          isMulti={false}
          isSearchable={false}
          name={`${name}-unit`}
          options={options}
          placeholder={intl.formatMessage({
            id: 'DatePeriodInput.placeholder.unit',
            defaultMessage: 'Unit',
            description: 'Placeholder for unit input in date period input',
          })}
          value={unit}
          width="input.s.width"
          onChange={handleUnitChange}
        />
      </Layout>
    );
  },
);
